import { useEffect, useState } from "react";
import { ArrowPathIcon } from "@heroicons/react/20/solid";
import { useLanguage } from "../../../contexts/LanguageContext";
import TagInput from "../../../components/TagInput";
import { generateClientId, generateClientSecret } from "../../../utils/credentials";
import { bookingkitTokenUrl, bookingkitWebhookUrl } from "../providers";
import {
  ErrorBox,
  Field,
  SecretInput,
  Section,
  SwitchRow,
  inputClass,
  outlineButtonClass,
} from "./ui";

// The provider sections only the edit drawer has. Field rules and the payload
// they feed live in ../providers.js.

function PassSelect({ id, passes, isLoading, value, onChange, required }) {
  const { t } = useLanguage();
  return (
    <select id={id} value={value} onChange={onChange} required={required} className={inputClass}>
      <option value="">{t("integrations.fields.selectCard")}</option>
      {isLoading ? (
        <option disabled>{t("integrations.fields.loadingCards")}</option>
      ) : (
        passes?.map((pass) => (
          <option key={pass.id} value={pass.id}>
            {pass.name}
          </option>
        ))
      )}
    </select>
  );
}

export function ShopifySection({ values, setValue, passes, isPassesLoading }) {
  const { t } = useLanguage();
  return (
    <>
      <SwitchRow
        id="shopify-customer-integration"
        title={t("integrations.customerIntegration.title")}
        description={t("integrations.customerIntegration.description")}
        checked={values.enableCustomerIntegration}
        onChange={(on) => setValue("enableCustomerIntegration", on)}
      >
        {values.enableCustomerIntegration && (
          <Field label={t("integrations.customerIntegration.selectCard")} htmlFor="shopify-pass">
            <PassSelect
              id="shopify-pass"
              passes={passes}
              isLoading={isPassesLoading}
              value={values.passId}
              onChange={(e) => setValue("passId", e.target.value)}
              required
            />
          </Field>
        )}
      </SwitchRow>
      <Field
        label={t("integrations.customerIntegration.webhookSecret")}
        htmlFor="shopify-webhook-secret"
        hint={t("integrations.fields.webhookSecretHint")}
      >
        <SecretInput
          id="shopify-webhook-secret"
          value={values.webhookSecret}
          onChange={(e) => setValue("webhookSecret", e.target.value)}
          placeholder={t("integrations.fields.webhookSecretPlaceholder")}
          required={values.enableCustomerIntegration}
        />
      </Field>
    </>
  );
}

export function CustomerSyncSection({
  values,
  setValue,
  audiences,
  isAudiencesLoading,
  listName,
  errorLines,
}) {
  const { t } = useLanguage();
  const list = audiences?.audiences;

  // Older configs saved only the list's name; pick the matching audience.
  useEffect(() => {
    if (list?.length && listName && !values.selectedAudience) {
      const match = list.find((a) => a.name === listName);
      if (match) setValue("selectedAudience", match.id);
    }
  }, [list, listName, values.selectedAudience, setValue]);

  return (
    <SwitchRow
      id="customer-sync"
      title={t("integrations.sendCustomerInfo.title")}
      description={t("integrations.sendCustomerInfo.description")}
      checked={values.sendCustomerEnabled}
      onChange={(on) => setValue("sendCustomerEnabled", on)}
    >
      {values.sendCustomerEnabled && <ErrorBox lines={errorLines} />}
      {values.sendCustomerEnabled && (
        <Field label={t("integrations.audienceListName")} htmlFor="customer-sync-audience">
          <select
            id="customer-sync-audience"
            value={values.selectedAudience}
            onChange={(e) => setValue("selectedAudience", e.target.value)}
            className={inputClass}
          >
            <option value="">{t("integrations.fields.selectAudience")}</option>
            {isAudiencesLoading ? (
              <option disabled>{t("integrations.fields.loadingAudiences")}</option>
            ) : (
              list?.map((audience) => (
                <option key={audience.id} value={audience.id}>
                  {audience.name}
                </option>
              ))
            )}
          </select>
        </Field>
      )}
    </SwitchRow>
  );
}

function CopyRow({ label, value, secret, onRegenerate }) {
  const { t } = useLanguage();
  const [copied, setCopied] = useState(false);
  const [shown, setShown] = useState(!secret);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  };

  return (
    <div className="flex flex-col gap-1">
      <span className="flex items-center justify-between gap-2">
        <span className="text-xs font-semibold text-gray-700">{label}</span>
        {onRegenerate && (
          <button
            type="button"
            onClick={onRegenerate}
            className="inline-flex min-h-7 items-center gap-1 text-xs font-medium text-indigo-700 hover:text-indigo-900"
          >
            <ArrowPathIcon className="h-3.5 w-3.5" aria-hidden="true" />
            {t("integrations.bookingkit.regenerate")}
          </button>
        )}
      </span>
      <span className="flex items-center gap-2">
        <span className="min-w-0 flex-1 rounded-lg border border-gray-200 bg-gray-50 px-2.5 py-2 font-mono text-xs break-all text-gray-900">
          {shown ? value : "•".repeat(24)}
        </span>
        {secret && (
          <button
            type="button"
            onClick={() => setShown((s) => !s)}
            aria-pressed={shown}
            className={`${outlineButtonClass} min-h-9 min-w-16 px-2.5 text-xs font-medium`}
          >
            {shown ? t("integrations.fields.hide") : t("integrations.fields.show")}
          </button>
        )}
        <button
          type="button"
          onClick={copy}
          aria-label={t("integrations.bookingkit.copyLabel", { label })}
          className={`${outlineButtonClass} min-h-9 min-w-16 px-2.5 text-xs font-medium`}
        >
          {copied ? t("integrations.bookingkit.copied") : t("integrations.bookingkit.copy")}
        </button>
      </span>
    </div>
  );
}

function IdList({ id, label, required, hint, emptyText, values, onChange, placeholder }) {
  const { t } = useLanguage();
  const missing = required && values.length === 0;
  return (
    <div
      className={`flex flex-col gap-2 rounded-xl bg-gray-50 px-4 py-3.5 ${
        missing ? "border-2 border-red-700" : "border border-gray-200"
      }`}
    >
      <span className="flex flex-wrap items-baseline justify-between gap-1.5">
        <span id={`${id}-label`} className="text-sm font-semibold text-gray-900">
          {label}
        </span>
        <span className={`text-xs font-semibold ${missing ? "text-red-700" : "text-gray-600"}`}>
          {required ? t("integrations.bookingkit.required") : t("integrations.bookingkit.optional")}
        </span>
      </span>
      <span className="text-xs leading-normal text-gray-700">{hint}</span>
      {values.length === 0 && <span className="text-[13px] text-gray-600">{emptyText}</span>}
      <TagInput values={values} onChange={onChange} placeholder={placeholder} />
    </div>
  );
}

export function BookingkitSection({ values, setValue, passes, isPassesLoading }) {
  const { t } = useLanguage();
  return (
    <>
      <SwitchRow
        id="bookingkit-enabled"
        title={t("integrations.bookingkit.enabled")}
        description={t("integrations.bookingkit.enabledDescription")}
        checked={values.bkEnabled}
        onChange={(on) => setValue("bkEnabled", on)}
      />

      <Field
        label={t("integrations.bookingkit.loyaltyCard")}
        htmlFor="bookingkit-pass"
        hint={t("integrations.bookingkit.loyaltyCardHint")}
      >
        <PassSelect
          id="bookingkit-pass"
          passes={passes}
          isLoading={isPassesLoading}
          value={values.passId}
          onChange={(e) => setValue("passId", e.target.value)}
          required
        />
      </Field>

      <Section
        title={t("integrations.bookingkit.credentialsTitle")}
        description={t("integrations.bookingkit.credentialsDescription")}
      >
        <CopyRow
          label={t("integrations.bookingkit.clientId")}
          value={values.bkOauthClientId}
          onRegenerate={() => setValue("bkOauthClientId", generateClientId())}
        />
        <CopyRow
          label={t("integrations.bookingkit.clientSecret")}
          value={values.bkOauthClientSecret}
          secret
          onRegenerate={() => setValue("bkOauthClientSecret", generateClientSecret())}
        />
        <p className="text-xs leading-normal text-gray-600">
          {t("integrations.bookingkit.regenerateHint")}
        </p>
        <CopyRow label={t("integrations.bookingkit.tokenUrl")} value={bookingkitTokenUrl()} />
        <CopyRow label={t("integrations.bookingkit.webhookUrl")} value={bookingkitWebhookUrl()} />
      </Section>

      <IdList
        id="bookingkit-vendors"
        label={t("integrations.bookingkit.vendorIds")}
        required
        hint={t("integrations.bookingkit.vendorIdsHint")}
        emptyText={t("integrations.bookingkit.vendorIdsEmpty")}
        values={values.bkAllowedVendorIds}
        onChange={(list) => setValue("bkAllowedVendorIds", list)}
        placeholder={t("integrations.bookingkit.vendorIdsPlaceholder")}
      />
      <IdList
        id="bookingkit-products"
        label={t("integrations.bookingkit.productIds")}
        hint={t("integrations.bookingkit.productIdsHint")}
        emptyText={t("integrations.bookingkit.productIdsEmpty")}
        values={values.bkAllowedProductIds}
        onChange={(list) => setValue("bkAllowedProductIds", list)}
        placeholder={t("integrations.bookingkit.productIdsPlaceholder")}
      />
      <p
        role="note"
        className="rounded-lg bg-amber-50 px-3 py-2.5 text-xs leading-normal text-amber-900"
      >
        {t("integrations.bookingkit.wrongListNote")}
      </p>

      <Field
        label={t("integrations.bookingkit.statuses")}
        hint={t("integrations.bookingkit.statusesHint")}
      >
        <TagInput
          values={values.bkEligibleStatuses}
          onChange={(list) => setValue("bkEligibleStatuses", list)}
          placeholder="ACCEPTED"
        />
      </Field>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Field label={t("integrations.bookingkit.currency")} htmlFor="bookingkit-currency">
          <input
            id="bookingkit-currency"
            type="text"
            value={values.bkExpectedCurrency}
            onChange={(e) => setValue("bkExpectedCurrency", e.target.value.toUpperCase())}
            placeholder="EUR"
            maxLength={3}
            autoComplete="off"
            className={`${inputClass} uppercase`}
          />
        </Field>
        <Field label={t("integrations.bookingkit.graceDays")} htmlFor="bookingkit-grace">
          <input
            id="bookingkit-grace"
            type="number"
            min={1}
            max={365}
            value={values.bkClaimGraceDays}
            onChange={(e) => setValue("bkClaimGraceDays", Number(e.target.value) || 0)}
            className={inputClass}
          />
        </Field>
      </div>
      <p className="-mt-2 text-xs leading-normal text-gray-600">
        {t("integrations.bookingkit.graceDaysHint")}
      </p>
    </>
  );
}
